import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Container,
    Header,
    Modal,
    SpaceBetween,
    StatusIndicator
} from "@amzn/awsui-components-react";
import { EntityFactory, IPage, Page } from "@amzn/ask-legal-domain";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { ErrorFlashbar } from "../common/ErrorFlashbar";

/**
 * Page-level Knowledge Base opt-in. Including a page requires an explicit risk
 * acknowledgment; removing it does not (removal only ever narrows exposure).
 * Until auto-sync ships (M3), the flag is what the next sync run picks up.
 */
export const KnowledgeBaseOptInSection = (props: {
    page: Page.Data;
    /** True when the page has viewer restrictions - the acknowledgment warns louder. */
    isRestricted: boolean;
    onUpdate: (updated: Page.Data) => void;
}) => {
    const context = React.useContext(AppContext);
    const [showModal, setShowModal] = React.useState(false);
    const updateRunner = useAPI(context.getPageAPI().updateMetadata);

    const isIncluded = props.page.includeInKB === true;

    const submit = (includeInKB: boolean) => {
        updateRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                targetRef: EntityFactory.toEntityRef(props.page),
                includeInKB: includeInKB
            })
        );
    };

    React.useEffect(() => {
        if (updateRunner.status === "Succeeded") {
            props.onUpdate(updateRunner.data.output);
        }
    }, [updateRunner.status]);

    return (
        <React.Fragment>
            {updateRunner.status === "Error" && <ErrorFlashbar error={updateRunner.data.err} />}
            <Container
                header={
                    <Header
                        variant="h3"
                        actions={
                            <Button
                                onClick={() => isIncluded ? submit(false) : setShowModal(true)}
                                loading={updateRunner.status === "Running"}
                                variant={isIncluded ? "normal" : "primary"}
                            >
                                {isIncluded ? "Remove from Knowledge Base" : "Include in Knowledge Base"}
                            </Button>
                        }
                    >
                        Pathfinder KB
                    </Header>
                }
            >
                {isIncluded ? (
                    <StatusIndicator type="success">
                        This page is included in the team&apos;s Knowledge Base and can be surfaced by AI Teammate.
                    </StatusIndicator>
                ) : (
                    <Box variant="p" color="text-body-secondary">
                        Include this page in the team&apos;s Knowledge Base so AI Teammate can answer
                        questions from its published content.
                    </Box>
                )}
            </Container>
            <Modal
                visible={showModal}
                onDismiss={() => setShowModal(false)}
                header="Include this page in the Knowledge Base"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setShowModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => { setShowModal(false); submit(true); }}
                            >
                                I acknowledge
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <Alert type={props.isRestricted ? "error" : "warning"}>
                        {(props.isRestricted
                            ? "This page has viewer restrictions. "
                            : "") +
                            "Important: once a page is opted in and an AI Teammate is granted access to " +
                            "this team's Knowledge Base, the page's published content becomes retrievable " +
                            "through that AI Teammate - regardless of the page's PathFinder permissions " +
                            "(\"Legal Only\" and viewer restrictions do not carry over). Who can see it is " +
                            "determined by where that AI Teammate is deployed - for example, every member " +
                            "of a Slack channel it is added to - not by PathFinder. Only opt in pages that " +
                            "are safe for that audience."}
                    </Alert>
                    <Box variant="p">
                        Restricted inline sections are never included. Attachments are indexed by file
                        name only, images are not indexed, and smart tables are omitted. The Knowledge
                        Base is updated as this page is published or changed.
                    </Box>
                </SpaceBetween>
            </Modal>
        </React.Fragment>
    );
};
